import React, { useState, useRef } from 'react';
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import { Typography, CircularProgress } from "@mui/material";
import Card from "@mui/material/Card";
import MDTypography from "components/MDTypography";
import { generateColors } from "utils/chartColors";

// -----------------------------------------------------------
// 🚀 MAIN COMPONENT
// -----------------------------------------------------------

function AudienceGrowthChart({
    title,
    datasets,
    previousdatasets,
    isLoading,
    entitlements,
    viewMode,
    onViewModeChange,
    profileColorMap = {}
}) {
    // ---------------------------------------------------------------------
    // CONDITION CHECK: Only Visible if ALL profiles are Facebook & Own
    // "Own" is determined by the presence of granular metrics (gain_paid/organic) usually,
    // or we can pass an 'isOwn' flag. The user said "visible when all selected profiles are facebook and own profiles".
    // ---------------------------------------------------------------------

    const safeDatasets = datasets || [];

    const isEligible = safeDatasets.length > 0 && safeDatasets.every(profile => {
        // 1. Must be Facebook
        if (profile.platform !== 'Facebook') return false;

        // 2. Must be "Own" Data (Check for granular fields usually present in own-data)
        // Adjust these keys based on what your backend actually returns for 'own' data
        const hasGranular = profile.data && profile.data.some(d =>
            (d.profileMetrics?.followers_gain_paid !== undefined) ||
            (d.profileMetrics?.followers_gain_organic !== undefined) ||
            (d.pageMetrics?.followers_gain_paid !== undefined) ||
            (d.pageMetrics?.followers_gain_organic !== undefined)
        );
        return hasGranular;
    });

    if (!isEligible && !isLoading) {
        // If not eligible (e.g. mixed platforms or competitors), return null or empty wrapper
        // User said "Only visible when...", so returning null hides it.
        // However, usually we might want to return nothing to let the parent handle layout,
        // but if this component is mounted unconditionally, returning null is safe.
        // Let's return null.
        return null;
    }

    // ---------------------------------------------------------------------
    // RENDER LOADER
    // ---------------------------------------------------------------------
    const renderLoader = () => (
        <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
                borderRadius: "0.75rem",
            }}
        >
            <CircularProgress size={32} thickness={4} color="accent" />
        </MDBox>
    );


    const safePreviousDatasets = previousdatasets || [];

    // Align labels
    const chartLabels = safeDatasets.length > 0 ? safeDatasets[0].data.map((item) => item.date) : [];

    // Title
    const derivedTitle = title || "Audience Growth (Paid vs Organic)";

    // ⬇️=========================================
    // ⬇️ DATA PROCESSING
    // ⬇️=========================================

    const processDataset = (inputDatasets) => {
        const resultDatasets = [];

        // Generate colors if map is missing (fallback)
        const profileIds = inputDatasets.map(d => d.profileId);
        const generatedColors = generateColors(inputDatasets.length, profileIds);

        inputDatasets.forEach((profile, index) => {
            const organicData = [];
            const paidData = [];
            const organicFlags = [];

            // Get Base Color
            // If profileColorMap has it, use it. Else use generated.
            // profileColorMap values are usually Hex strings like "#123456"
            const baseColorHex = profileColorMap[profile.profileId] || generatedColors[index] || "#3b5998";

            // Helper to add opacity
            const hexToRgba = (hex, alpha) => {
                let r = 0, g = 0, b = 0;
                // Handle different formats if needed, assuming standard hex 6 or 3 chars
                if (hex.startsWith('#')) hex = hex.slice(1);
                if (hex.length === 3) {
                    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
                }
                if (hex.length === 6) {
                    r = parseInt(hex.slice(0, 2), 16);
                    g = parseInt(hex.slice(2, 4), 16);
                    b = parseInt(hex.slice(4, 6), 16);
                }
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            };

            const COLOR_ORGANIC = hexToRgba(baseColorHex, 0.9); // High Opacity
            const COLOR_PAID = hexToRgba(baseColorHex, 0.4);    // Low Opacity

            profile.data.forEach(item => {
                const metrics = item.profileMetrics || item.pageMetrics || {};

                // Extract Organic vs Paid Gain
                // Note: Ensure we look for 'gain', not 'reach'. The chart name is "Audience Growth".
                const paidGain = metrics.followers_gain_paid || 0;
                const organicGain = metrics.followers_gain_organic ||
                    // Fallback if total gain is present but organic is not explicitly separated
                    ((metrics.followers_gain || 0) - paidGain);

                organicData.push(organicGain);
                paidData.push(paidGain);
                organicFlags.push(false);
            });

            // Stack ID: Unique per profile to stack that profile's Paid on top of its Organic
            // BUT usually we want side-by-side bars for different profiles, each stacked.
            // Chart.js 'stack' property groups bars.
            // - If all have same stack ID, they form one giant bar.
            // - If each profile has unique stack ID, they stand side-by-side.
            const stackId = `stack-${profile.profileId}`;

            // 1. Organic Bar (Base)
            resultDatasets.push({
                type: 'bar',
                label: profile.label || profile.name, // ✅ Legend: Just Profile Name
                data: organicData,
                backgroundColor: COLOR_ORGANIC,
                borderColor: baseColorHex,
                borderWidth: 1,
                stack: stackId,
                profileId: profile.profileId,
                profilePictureUrl: profile.profilePictureUrl,
                platform: profile.platform, // ✅ Added for Legend Icon
                metricName: "Organic Growth",
            });

            // 2. Paid Bar (Stacked on top)
            resultDatasets.push({
                type: 'bar',
                label: profile.label || profile.name, // ✅ Legend: Just Profile Name (Merges with above)
                data: paidData,
                backgroundColor: COLOR_PAID,
                borderColor: baseColorHex,
                borderWidth: 1,
                stack: stackId,
                profileId: profile.profileId,
                platform: profile.platform, // ✅ Added (hidden but good practice)
                metricName: "Paid Growth",
                skipMetricCard: true,
                skipLegend: true, // ✅ HIDE from Legend (Matches AudienceChangeGainLossChart behavior)
            });
        });

        return resultDatasets;
    };

    const chartDatasets = processDataset(safeDatasets);
    const previousChartDatasets = processDataset(safePreviousDatasets); // ✅ Process Previous Data

    // ✅ Construct Chart Data Object
    const computedChartData = {
        labels: chartLabels,
        datasets: chartDatasets,
        previousdatasets: previousChartDatasets, // ✅ Added for Comparison
    };

    // -----------------------------------------------------------
    // PERSISTENCE & LOADING
    // -----------------------------------------------------------
    const lastChartDataRef = useRef(null);
    if (safeDatasets.length > 0) {
        lastChartDataRef.current = computedChartData;
    }

    let dataToRender = computedChartData;
    if (isLoading && safeDatasets.length === 0 && lastChartDataRef.current) {
        dataToRender = lastChartDataRef.current;
    }

    const hasData = (dataToRender.datasets && dataToRender.datasets.length > 0);

    // -----------------------------------------------------------
    // CONFIGURATION OVERRIDES
    // -----------------------------------------------------------

    const overrideOptions = {
        interaction: {
            mode: 'index',
            intersect: false,
        },
        scales: {
            y: {
                title: {
                    display: true,
                    text: "New Followers"
                },
                stacked: true, // ✅ IMPORTANT: Enable Stacking on Y
                grid: {
                    drawOnChartArea: true,
                    color: 'rgba(0,0,0,0.1)',
                },
                ticks: {
                    callback: (val) => val.toLocaleString()
                }
            },
            x: {
                stacked: true, // ✅ IMPORTANT: Enable Stacking on X
            }
        },
        plugins: {
            tooltip: {
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.metricName || context.dataset.label || '';
                        let value = context.parsed.y;
                        return `${label}: ${value.toLocaleString()}`;
                    },
                    footer: (tooltipItems) => {
                        let total = 0;
                        tooltipItems.forEach(function (tooltipItem) {
                            total += tooltipItem.parsed.y;
                        });
                        return 'Total: ' + total.toLocaleString();
                    }
                }
            }
        }
    };

    // -----------------------------------------------------------
    // TOOLTIP DESCRIPTION
    // -----------------------------------------------------------

    const tooltipContent = (
        <div style={{ maxWidth: "350px", textAlign: "left" }}>
            <Typography variant="body2" gutterBottom>
                <strong>Description:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                This chart displays the daily growth of your audience, breakdown by <strong>Organic</strong> (free) and <strong>Paid</strong> (ads) sources.
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>How to read the chart:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                - <strong>Organic (Darker)</strong>: Followers gained naturally without ad spend.<br />
                - <strong>Paid (Lighter)</strong>: Followers gained directly from advertisements.<br />
                - <strong>Stacked Bars</strong>: The total height represents the total new followers for the day.
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>Insights you can gain:</strong>
            </Typography>
            <Typography variant="caption" display="block">
                - Measure the effectiveness (ROI) of your paid campaigns.<br />
                - Compare organic viral growth vs paid acquisition.<br />
                - Identify days where ads drove majority of the growth.<br />
                - Monitor the balance between paid and organic strategies.
            </Typography>
        </div>
    );

    return (
        <div style={{ position: "relative" }}>
            {isLoading && renderLoader()}

            {hasData ? (
                <DefaultLineChart
                    title={derivedTitle}
                    description={tooltipContent}
                    height="19.125rem"
                    chart={dataToRender}
                    rawData={safeDatasets}
                    entitlements={entitlements}
                    isSnapshot={false}
                    viewMode={viewMode}
                    onViewModeChange={onViewModeChange}
                    overrideOptions={overrideOptions}
                    aggregate={true}
                    profileColorMap={profileColorMap}
                />
            ) : (
                <Card sx={{ height: "19.125rem" }}>
                    {!isLoading && (
                        <MDTypography variant="body2" color="text" sx={{ p: 3, textAlign: "center" }}>
                            No granular growth data available (Paid/Organic).
                        </MDTypography>
                    )}
                </Card>
            )}
        </div>
    );
}

AudienceGrowthChart.propTypes = {
    title: PropTypes.string,
    datasets: PropTypes.array,
    previousdatasets: PropTypes.array,
    isLoading: PropTypes.bool,
    entitlements: PropTypes.object,
    viewMode: PropTypes.string,
    onViewModeChange: PropTypes.func,
    profileColorMap: PropTypes.object,
};

export default AudienceGrowthChart;
